/**
 * Permissions (SU-6, W7, ADR-0049): the permission matrix of an organisation,
 * behind its flag permissions_v2.
 *
 *   - rows = the 22 permissions, columns = the three roles; a cell is a switch
 *     when the role holds the permission today, « — » otherwise (the matrix
 *     only takes away, it never gives beyond today's behaviour);
 *   - a tenant admin edits the Commercial and Lecture columns of its own
 *     organisation, within what it holds; a super admin edits everything,
 *     for any organisation (picker), and also switches the two W7 flags;
 *   - every change is reversible: it applies at once and the toast offers
 *     « Annuler » (DES-13), no confirmation;
 *   - the page says plainly when the matrix is not applied (flag off).
 *
 * Server: GET/PATCH /api/permissions/organisations/:id, PATCH …/flags.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { ErrorLine, PlatformPageHeader } from '@/components/backoffice/platform/PlatformBits';
import { showErrorToast } from '@/lib/errorToast';
import { useAuthStore } from '@/stores/authStore';
import { listOrganisations } from '@/api/endpoints/admin';
import {
  getPermissionMatrix, setW7Flags, updatePermissionMatrix,
  type MatrixRole, type Permission, type PermissionMatrix, type W7Flags,
} from '@/api/endpoints/permissions';
import { MY_PERMISSIONS_KEY } from '@/hooks/usePermissionMatrix';

const ROLES: MatrixRole[] = ['tenant_admin', 'caller', 'viewer'];
const permKey = (p: string) => p.replace(/\./g, '_');

function OrganisationPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const { t } = useTranslation(['admin']);
  const orgs = useQuery({ queryKey: ['admin', 'organisations', 'picker'], queryFn: () => listOrganisations() });
  if (orgs.isError) return <ErrorLine message={t('admin:permissionsMatrix.orgsError')} error={orgs.error} onRetry={() => void orgs.refetch()} />;
  return (
    <label className="flex flex-col gap-1 text-sm sm:max-w-sm">
      <span className="font-medium">{t('admin:permissionsMatrix.organisation')}</span>
      <select
        className="h-11 rounded-vf border px-3 text-sm"
        style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-bg)', color: 'var(--vf-text-1)' }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={orgs.isLoading}
      >
        {!value && <option value="">{t('admin:permissionsMatrix.pickOrganisation')}</option>}
        {/* QA-W7-11: a soft-deleted organisation has nothing to set. */}
        {(orgs.data?.organisations ?? []).filter((o) => o.status !== 'deleted').map((o) => <option key={o.id} value={o.id}>{o.company_name || o.email}</option>)}
      </select>
    </label>
  );
}

function FlagSwitches({ organisationId, flags, onChanged }: { organisationId: string; flags: W7Flags; onChanged: (organisationId: string, f: W7Flags) => void }) {
  const { t } = useTranslation(['admin', 'common']);
  // REV-W7-F2: the organisation travels with the mutation, so an « Annuler »
  // clicked after switching organisation still targets the first one.
  const mutation = useMutation({
    mutationFn: (v: { organisationId: string; patch: Partial<W7Flags> }) => setW7Flags(v.organisationId, v.patch),
  });
  const set = (key: keyof W7Flags, value: boolean) => {
    const label = t(`admin:permissionsMatrix.flags.${key}`);
    const target = organisationId;
    mutation.mutate({ organisationId: target, patch: { [key]: value } }, {
      onSuccess: ({ flags: after }) => {
        onChanged(target, after);
        toast.success(t(value ? 'admin:permissionsMatrix.flags.enabled' : 'admin:permissionsMatrix.flags.disabled', { flag: label }), {
          action: {
            label: t('common:actions.undo'),
            onClick: () => mutation.mutate(
              { organisationId: target, patch: { [key]: !value } },
              { onSuccess: ({ flags: back }) => onChanged(target, back), onError: (err) => showErrorToast(err) },
            ),
          },
        });
      },
      onError: (err) => showErrorToast(err),
    });
  };
  return (
    <section aria-labelledby="perm-flags" className="space-y-3 rounded-vf border p-4" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-bg)' }}>
      <h2 id="perm-flags" className="text-base font-semibold">{t('admin:permissionsMatrix.flags.title')}</h2>
      {(['permissions_v2', 'supervision_live'] as const).map((key) => (
        <label key={key} className="flex min-h-11 items-start gap-3 text-sm">
          <Switch checked={flags[key]} disabled={mutation.isPending} onCheckedChange={(v: boolean) => set(key, v)} aria-label={t(`admin:permissionsMatrix.flags.${key}`)} className="mt-0.5" />
          <span>
            <span className="block font-medium">{t(`admin:permissionsMatrix.flags.${key}`)}</span>
            <span className="block text-xs" style={{ color: 'var(--vf-text-2)' }}>{t(`admin:permissionsMatrix.flags.${key}Hint`)}</span>
          </span>
        </label>
      ))}
    </section>
  );
}

export default function AdminPermissions() {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin === true);
  const activeOrgId = useAuthStore((s) => s.activeMembership?.organisationId ?? '');
  const [orgId, setOrgId] = useState(activeOrgId);
  useEffect(() => { if (!orgId && activeOrgId) setOrgId(activeOrgId); }, [activeOrgId, orgId]);

  const matrixKey = (id: string) => ['permissions', 'matrix', id];
  const matrix = useQuery({ queryKey: matrixKey(orgId), queryFn: () => getPermissionMatrix(orgId), enabled: !!orgId });
  // REV-W7-F2: every write names its organisation; the cache entry written is
  // that organisation's, never the one on screen when the toast is clicked.
  const put = (id: string, next: PermissionMatrix) => {
    qc.setQueryData(matrixKey(id), next);
    void qc.invalidateQueries({ queryKey: MY_PERMISSIONS_KEY });
  };
  const putFlags = (id: string, flags: W7Flags) => {
    qc.setQueryData<PermissionMatrix>(matrixKey(id), (prev) => (prev ? { ...prev, flags } : prev));
    void qc.invalidateQueries({ queryKey: MY_PERMISSIONS_KEY });
  };

  const change = useMutation({
    mutationFn: (c: { organisationId: string; role: MatrixRole; permission: Permission; granted: boolean }) =>
      updatePermissionMatrix(c.organisationId, { [c.role]: { [c.permission]: c.granted } }),
  });
  const toggle = (role: MatrixRole, permission: Permission, granted: boolean) => {
    const label = t(`admin:permissionsMatrix.permissions.${permKey(permission)}.label`);
    const roleLabel = t(`admin:permissionsMatrix.roles.${role}`);
    const target = orgId;
    change.mutate({ organisationId: target, role, permission, granted }, {
      onSuccess: (next) => {
        put(target, next);
        toast.success(t(granted ? 'admin:permissionsMatrix.restored' : 'admin:permissionsMatrix.revoked', { permission: label, role: roleLabel }), {
          action: {
            label: t('common:actions.undo'),
            onClick: () => change.mutate(
              { organisationId: target, role, permission, granted: !granted },
              { onSuccess: (back) => put(target, back), onError: (err) => showErrorToast(err) },
            ),
          },
        });
      },
      onError: (err) => showErrorToast(err),
    });
  };

  const data = matrix.data;
  const revokedCount = useMemo(() => {
    if (!data) return 0;
    let n = 0;
    for (const r of ROLES) for (const p of data.permissions) if (data.defaults[r][p] && !data.effective[r][p]) n += 1;
    return n;
  }, [data]);

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-4 sm:p-6">
      <PlatformPageHeader title={t('admin:superAdminLayout.nav.permissions')} subtitle={t('admin:permissionsMatrix.subtitle')} />

      {isSuperAdmin && <OrganisationPicker value={orgId} onChange={setOrgId} />}

      {!orgId && !isSuperAdmin && <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('admin:permissionsMatrix.noOrganisation')}</p>}

      {matrix.isLoading && (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-6 w-6 animate-spin motion-reduce:animate-none" style={{ color: 'var(--vf-accent)' }} aria-label={t('common:actions.loading')} />
        </div>
      )}
      {matrix.isError && <ErrorLine message={t('admin:permissionsMatrix.loadError')} error={matrix.error} onRetry={() => void matrix.refetch()} />}

      {data && (
        <>
          {isSuperAdmin && (
            <FlagSwitches organisationId={orgId} flags={data.flags} onChanged={putFlags} />
          )}

          <p role="status" className="rounded-vf border px-3 py-2 text-sm" style={{ borderColor: 'var(--vf-border)', background: data.flags.permissions_v2 ? 'var(--vf-accent-bg)' : 'var(--vf-surface-2)' }}>
            {data.flags.permissions_v2
              ? t('admin:permissionsMatrix.applied', { count: revokedCount })
              : t('admin:permissionsMatrix.notApplied')}
          </p>

          {/* QA-W7-12: below 640 px the role columns scroll; say so, and keep
              the permission names in view (sticky first column). */}
          <p data-testid="matrix-scroll-cue" className="text-xs sm:hidden" style={{ color: 'var(--vf-text-2)' }}>{t('admin:permissionsMatrix.scrollCue')}</p>
          <div className="relative overflow-x-auto rounded-vf border" style={{ borderColor: 'var(--vf-border)' }}>
            <table className="w-full min-w-[560px] text-sm" aria-busy={change.isPending || undefined}>
              <caption className="sr-only">{t('admin:permissionsMatrix.caption')}</caption>
              <thead>
                <tr style={{ background: 'var(--vf-surface-2)' }}>
                  <th scope="col" className="sticky left-0 z-[1] px-3 py-2 text-left font-medium" style={{ background: 'var(--vf-surface-2)' }}>{t('admin:permissionsMatrix.permission')}</th>
                  {ROLES.map((r) => <th key={r} scope="col" className="w-28 px-3 py-2 text-center font-medium">{t(`admin:permissionsMatrix.roles.${r}`)}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.permissions.map((p) => (
                  <tr key={p} className="border-t" style={{ borderColor: 'var(--vf-border)' }}>
                    <th scope="row" className="sticky left-0 z-[1] w-40 max-w-[10rem] px-3 py-2 text-left font-normal sm:w-auto sm:max-w-none" style={{ background: 'var(--vf-bg)' }}>
                      <span className="block font-medium">{t(`admin:permissionsMatrix.permissions.${permKey(p)}.label`)}</span>
                      <span className="block text-xs" style={{ color: 'var(--vf-text-2)' }}>{t(`admin:permissionsMatrix.permissions.${permKey(p)}.hint`)}</span>
                    </th>
                    {ROLES.map((r) => {
                      const label = `${t(`admin:permissionsMatrix.permissions.${permKey(p)}.label`)} · ${t(`admin:permissionsMatrix.roles.${r}`)}`;
                      if (!data.defaults[r][p]) {
                        return <td key={r} className="px-3 py-2 text-center" style={{ color: 'var(--vf-text-3)' }} title={t('admin:permissionsMatrix.notDefault')}><span aria-label={`${label} : ${t('admin:permissionsMatrix.notDefault')}`}>—</span></td>;
                      }
                      const locked = data.locked.some((l) => l.role === r && l.permission === p);
                      const editable = data.editable[r][p];
                      return (
                        <td key={r} className="px-3 py-2">
                          <div className="flex min-h-11 items-center justify-center gap-1" title={locked ? t('admin:permissionsMatrix.locked') : editable ? undefined : t('admin:permissionsMatrix.readOnly')}>
                            <Switch
                              checked={data.effective[r][p]}
                              disabled={!editable || change.isPending}
                              onCheckedChange={(v: boolean) => toggle(r, p, v)}
                              aria-label={label}
                            />
                            {locked && <Lock className="h-3.5 w-3.5" aria-label={t('admin:permissionsMatrix.locked')} />}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs" style={{ color: 'var(--vf-text-2)' }}>{t(isSuperAdmin ? 'admin:permissionsMatrix.footSuper' : 'admin:permissionsMatrix.footTenant')}</p>
        </>
      )}
    </div>
  );
}
